import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { HubstaffMeDTO } from '../models/hubstaff-me.model';
import {
  HubstaffOrganizationDTO,
  HubstaffProjectDTO,
  HubstaffTaskDTO,
  HubstaffTimeEntryDTO,
  TimeEntryMappingDTO,
} from '../models/hubstaff-entities.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { JiraWorklogDTO } from '../models/jira-worklog.model';

export interface TimerEntities {
  projects: HubstaffProjectDTO[];
  tasks: HubstaffTaskDTO[];
}

@Injectable()
export class HubstaffApiService {
  constructor(private apiService: ApiService, private http: HttpClient) {}

  async me(): Promise<HubstaffMeDTO> {
    return this.apiService.get<HubstaffMeDTO>('/me');
  }

  async deleteMe(): Promise<void> {
    return this.apiService.delete('/me');
  }

  async organizations(): Promise<HubstaffOrganizationDTO[]> {
    return this.apiService.get<HubstaffOrganizationDTO[]>('/organizations');
  }

  async projects(organizationId: string): Promise<HubstaffProjectDTO[]> {
    const url = `/organizations/${organizationId}/projects`;

    const projects = await this.apiService.get<HubstaffProjectDTO[]>(url);

    if (!projects) return [];

    return projects;
  }

  async tasks(project?: HubstaffProjectDTO): Promise<HubstaffTaskDTO[]> {
    const url = `/projects/${project?.id}/tasks`;
    const tasks = await this.apiService.get<HubstaffTaskDTO[]>(url);

    if (!tasks) return [];

    return tasks;
  }

  async timeEntries(
    organizationId: string,
    userId: string,
    projectIds: string[] = [],
    taskIds: string[] = [],
  ): Promise<HubstaffTimeEntryDTO[]> {
    const url = `/organizations/${organizationId}/time-entries?userId=${userId}&projectIds=${projectIds?.join(
      ',',
    )}&taskIds=${taskIds.join(',')}`;

    const timeEntries = await this.apiService.get<HubstaffTimeEntryDTO[]>(url);

    if (!timeEntries) return [];

    return timeEntries;
  }

  async timerEntities(organization: HubstaffOrganizationDTO, projectId: string): Promise<TimerEntities> {
    const params = new HttpParams();

    const entitiesWithDTO = await this.apiService.get<TimerEntities>(
      `/organizations/${organization.id}/timer-entities`,
      params.append('projectId', projectId),
    );

    const entities: TimerEntities = {
      projects: entitiesWithDTO && entitiesWithDTO.projects ? entitiesWithDTO.projects : [],
      tasks: entitiesWithDTO && entitiesWithDTO.tasks ? entitiesWithDTO.tasks : [],
    };

    return entities;
  }

  async startTimer(timerData: HubstaffTimeEntryDTO): Promise<HubstaffTimeEntryDTO> {
    const url = `/organizations/${timerData.organizationId}/time-entries`;
    const payload = {
      userId: timerData.userId,
      description: timerData.description,
      projectId: timerData.projectId,
      taskId: timerData.taskId,
      billable: timerData.billable,
      startTime: timerData.startsAt,
    };

    return this.apiService.post<HubstaffTimeEntryDTO>(url, payload);
  }

  async timeEntryMappings(issueKey: string): Promise<TimeEntryMappingDTO[]> {
    const url = `/issues/${issueKey}/entry-mappings`;

    const timeEntryMappers = await this.apiService.get<TimeEntryMappingDTO[]>(url);

    if (!timeEntryMappers) return [];

    return timeEntryMappers;
  }

  async deleteTimeEntryMapping(issueKey: string, timeEntry: HubstaffTimeEntryDTO): Promise<void> {
    const url = `/issues/${issueKey}/entry-mappings?timeEntryId=${timeEntry.id}`;

    return this.apiService.delete(url);
  }

  async addTimeEntryMapping(
    issueKey: string,
    timeEntry: HubstaffTimeEntryDTO,
    worklogItem: JiraWorklogDTO,
  ): Promise<void> {
    const url = `/issues/${issueKey}/entry-mappings`;

    return this.apiService.post(url, {
      timeEntry,
      worklogItemId: worklogItem.id,
    });
  }
}
